import ProposalState from "../constants/ProposalState";
import UserType from "../constants/UserType";
import { theme } from "../mui-customs/Theme";

function getStateData(
  state?: ProposalState | null,
  newText?: string,
  userType?: UserType
) {
  let color;
  let text;
  let description = "";
  let longDescription = "";

  switch (state) {
    case ProposalState.DRAFT:
      color = theme.grey2[500];
      text = newText || "Draft";
      description = "Proposals you’ve started but haven't sent yet.";
      longDescription = `
        <p>This is your <strong>private creative space</strong> where you can build and refine your proposals before they go live. No one—including the client—can see these drafts while you're working on them.</p>
        <p><strong>Professional Tips for your Draft:</strong></p>
        <ul>
          <li><strong>Customize your menu:</strong> Tailor your dishes to the client's specific preferences and dietary needs mentioned in the gig.</li>
          <li><strong>Detail your service:</strong> Clearly explain what is included (e.g., shopping, cooking, cleanup, service).</li>
          <li><strong>Check your availability:</strong> Ensure the date and time work perfectly with your schedule before committing.</li>
        </ul>
        <p>Once you are satisfied with the proposal, finalizing it will move it to the <strong>'Open Bid'</strong> stage, making it visible to the client for their review.</p>
      `;
      break;

    case ProposalState.OPEN_BID:
      color = theme.purple[300];
      text = newText || "Open Bid";
      if (userType === UserType.Chef) {
        description = "Bids you’ve sent that are currently being reviewed.";
        longDescription = `
          <p>These are your <strong>active proposals</strong> that have been sent to clients. At this stage, the client is actively comparing your menu, pricing, and background against other bids.</p>
          <p><strong>Success Strategies:</strong></p>
          <ul>
            <li><strong>Be Responsive:</strong> Clients often message with follow-up questions. Prompt replies significantly increase booking rates.</li>
            <li><strong>Communication:</strong> Use the messaging system to build rapport and demonstrate your culinary expertise.</li>
            <li><strong>Flexibility:</strong> Be prepared to adjust your proposal if the client requests minor tweaks to the menu or guest count.</li>
          </ul>
          <p>Your proposal remains editable until the client makes a final decision. Keep an eye on your notifications for any updates or messages!</p>
        `;
      } else {
        description = "Gigs with active bids from chefs for you to review.";
        longDescription = `
          <p><strong>You have received proposals!</strong> In this section, you can review, compare, and manage all incoming bids for your event.</p>
          <p><strong>How to choose the best Chef:</strong></p>
          <ul>
            <li><strong>Review the Menu:</strong> Look for creativity, ingredient quality, and alignment with your event's theme.</li>
            <li><strong>Check Profiles:</strong> Visit the chef's profile to see their professional background and reviews from other clients.</li>
            <li><strong>Message the Chef:</strong> Use the chat to ask about specific dietary requirements or to request adjustments to the proposal.</li>
          </ul>
          <p>Once you find the perfect match, simply <strong>accept their proposal</strong> to move forward with the booking and secure your date.</p>
        `;
      }
      break;

    case ProposalState.NEW_GIG:
      color = theme.brand2[500];
      text = newText || "New Gig";
      description = "Your new gig posts waiting for chef proposals.";
      longDescription = `
        <p>These are your <strong>newly published events</strong> that are currently awaiting their first proposals from our community of talented chefs.</p>
        <p><strong>What to expect:</strong></p>
        <ul>
          <li><strong>Notifications:</strong> We have already notified qualified chefs in your area about your event request.</li>
          <li><strong>Reviewing Period:</strong> It usually takes a short time for chefs to review your details and craft a customized menu.</li>
          <li><strong>Optimization:</strong> If you haven't received bids within 48 hours, consider reviewing your budget or adding more details to your menu preferences.</li>
        </ul>
        <p>As soon as a chef submits a bid, this gig will automatically move to your <strong>'Open Bid'</strong> section for review.</p>
      `;
      break;

    case ProposalState.ACCEPTED_BOOKED:
      color = theme.brand1[500];
      text = newText || "Accepted Booked";
      if (userType === UserType.Chef) {
        description = "Gigs where you are officially booked and confirmed.";
        longDescription = `
          <p><strong>Congratulations—the booking is confirmed!</strong> The client has selected your proposal, and this date is now officially locked in your professional schedule.</p>
          <p><strong>Next Steps for a 5-Star Experience:</strong></p>
          <ul>
            <li><strong>Logistics:</strong> Finalize arrival times, kitchen access, and specialized equipment needs with the client.</li>
            <li><strong>Sourcing:</strong> Begin coordinating with your trusted suppliers for the freshest ingredients.</li>
            <li><strong>Stay Connected:</strong> Maintain regular communication to ensure any last-minute guest changes are handled smoothly.</li>
          </ul>
          <p>Your commitment here is the foundation of a successful event. We wish you a fantastic service!</p>
        `;
      } else {
        description =
          "Gigs where you've chosen your chef and confirmed the booking.";
        longDescription = `
          <p><strong>Your event is officially booked!</strong> You have selected your chef, and they have reserved this date exclusively for your event.</p>
          <p><strong>Your Event Command Center:</strong></p>
          <ul>
            <li><strong>Coordination:</strong> Use the messaging system to discuss final details like arrival times and kitchen layout.</li>
            <li><strong>Menu Review:</strong> You can revisit the agreed-upon menu and pricing details here at any time.</li>
            <li><strong>Preparation:</strong> Inform the chef of any final changes to guest dietary requirements well in advance.</li>
          </ul>
          <p>With the booking secured, you can relax and look forward to an exceptional dining experience in your own space.</p>
        `;
      }
      break;

    case ProposalState.DEPOSIT_PAID:
      color = theme.brand1[500];
      text = newText || "Accepted Booked";
      if (userType === UserType.Chef) {
        description = "Confirmed bookings with a secured deposit.";
        longDescription = `
          <p><strong>Financial Security Confirmed!</strong> The client has successfully paid the deposit, providing full financial commitment to this gig.</p>
          <p><strong>Finalizing Preparations:</strong></p>
          <ul>
            <li><strong>Confirmed Commitment:</strong> The deposit ensures your time and services are protected.</li>
            <li><strong>Detailed Prep:</strong> You should now be in the final stages of ingredient sourcing and menu preparation.</li>
            <li><strong>Review:</strong> Double-check all event notes one last time to ensure no detail is overlooked.</li>
          </ul>
          <p>This stage represents a fully committed partnership between you and the client.</p>
        `;
      } else {
        description = "Secured bookings with processed deposit.";
        longDescription = `
          <p><strong>Your event is fully secured!</strong> Your deposit has been successfully processed, which means the chef is now officially committed to your event.</p>
          <p><strong>Peace of Mind:</strong></p>
          <ul>
            <li><strong>Guaranteed Service:</strong> The processed deposit confirms the chef's professional obligation to your date.</li>
            <li><strong>Final Details:</strong> This is the perfect time to chat with your chef about the finer points of service and presentation.</li>
            <li><strong>Receipts:</strong> You can review your transaction history and the finalized agreement in this section.</li>
          </ul>
          <p>Everything is set for your special occasion. We hope you have a wonderful event!</p>
        `;
      }
      break;

    case ProposalState.INVOICE_CHANGE:
      color = theme.yellow[500];
      text = newText || "Invoice Change";
      description = "Gigs with requested updates to pricing or details.";
      longDescription = `
        <p>This section tracks gigs where <strong>adjustments to the original agreement</strong> have been requested by either party.</p>
        <p><strong>Why changes happen:</strong></p>
        <ul>
          <li><strong>Guest Count:</strong> If the number of diners has increased or decreased.</li>
          <li><strong>Premium Additions:</strong> Requests for additional courses, upgraded ingredients, or extra services.</li>
          <li><strong>Logistical shifts:</strong> Changes in travel needs or service duration.</li>
        </ul>
        <p><strong>Important:</strong> Both the chef and the client must review and manually accept these changes. The gig will remain in this state until both parties agree to the new terms, after which it will return to its confirmed status.</p>
      `;
      break;

    case ProposalState.FINISHED_AND_PAID:
      color = theme.brand1[700];
      text = newText || "Finished & Paid";
      description = "Successfully completed and fully paid events.";
      longDescription = `
        <p>These are your <strong>completed milestones</strong>. The service has been delivered, and all payments have been finalized.</p>
        <p><strong>Build our Community:</strong></p>
        <ul>
          <li><strong>Leave a Review:</strong> Feedback is the lifeblood of our marketplace. High-quality reviews help great chefs and clients stand out.</li>
          <li><strong>Record Keeping:</strong> This serves as your permanent history of menus, pricing, and experiences.</li>
          <li><strong>Future Bookings:</strong> If you loved working together, you can easily find the profile here to reconnect for future events.</li>
        </ul>
        <p>We are thrilled to have been a part of your culinary journey!</p>
      `;
      break;

    case ProposalState.CANCELED_GIG:
      color = theme.red[300];
      text = newText || "Canceled Gig";
      description = "Booked events that were unfortunately canceled.";
      longDescription = `
        <p>This section contains records of events that were <strong>canceled after a formal booking</strong> was already in place.</p>
        <p><strong>Policy Details:</strong></p>
        <ul>
          <li><strong>Marketplace Rules:</strong> Cancellations are handled according to our standard policies to protect both parties.</li>
          <li><strong>Fees & Refunds:</strong> Any applicable cancellation fees or refunds are automatically processed by the system.</li>
          <li><strong>Support:</strong> If you have questions about a specific cancellation, please contact our support team.</li>
        </ul>
        <p>While we regret that this event didn't happen, we hope your next booking is a great success!</p>
      `;
      break;

    case ProposalState.CANCELED_BID:
      color = theme.red[300];
      text = newText || "Canceled Bid";
      description = "Proposals that were withdrawn before booking.";
      longDescription = `
        <p>These are bids that were <strong>withdrawn or canceled before any commitment</strong> was made.</p>
        <p><strong>Key Information:</strong></p>
        <ul>
          <li><strong>No Commitment:</strong> Since no booking occurred, no fees were charged.</li>
          <li><strong>History:</strong> We keep these records for your personal reference only.</li>
          <li><strong>Move Forward:</strong> Feel free to explore other open opportunities in the marketplace.</li>
        </ul>
        <p>Cancellations at this stage are a normal part of finding the perfect match.</p>
      `;
      break;

    case ProposalState.DECLINED_BID:
      color = theme.brand2[750];
      text = newText || "Declined Bid";
      description = "Bids that the client chose not to move forward with.";
      longDescription = `
        <p>In a competitive marketplace, clients often choose the proposal that most closely matches their specific vision for an event.</p>
        <p><strong>Perspectives:</strong></p>
        <ul>
          <li><strong>For Chefs:</strong> A declined bid is often just a matter of preference or budget fit. We encourage you to keep refining your pitch!</li>
          <li><strong>For Clients:</strong> This section keeps your active bidding area clean by archiving the proposals you didn't select.</li>
        </ul>
        <p>Every interaction is an opportunity to learn and grow within the community.</p>
      `;
      break;

    case ProposalState.PENDING:
      color = theme.purple[200];
      text = newText || "Pending";
      description = "Gigs waiting for processing or final confirmation.";
      longDescription = `
        <p>These gigs are in a <strong>temporary processing state</strong>, usually following a recent action.</p>
        <p><strong>Common Reasons:</strong></p>
        <ul>
          <li><strong>Payment Clearance:</strong> Waiting for our secure processor (Stripe) to finalize a transaction.</li>
          <li><strong>Verification:</strong> A quick administrative check to ensure all details are correct.</li>
          <li><strong>Grace Period:</strong> A short window allowing for final confirmations.</li>
        </ul>
        <p>Statuses usually update within minutes. If a gig stays here longer than 24 hours, please check your email for any verification requests.</p>
      `;
      break;

    case ProposalState.LIKED:
      color = theme.grey2[500];
      text = newText || "Liked";
      description = "Gigs or chefs you've saved for future reference.";
      longDescription = `
        <p>This is your <strong>curated shortlist</strong> of favorite professionals or interesting opportunities.</p>
        <p><strong>How to use this section:</strong></p>
        <ul>
          <li><strong>Shortlisting:</strong> Save chefs whose culinary style matches your long-term preferences.</li>
          <li><strong>Planning:</strong> Bookmark gigs you might want to bid on once your schedule is clearer.</li>
          <li><strong>Quick Access:</strong> Avoid searching the entire marketplace again by keeping your top choices here.</li>
        </ul>
        <p>You can manage this list easily by clicking the heart icon anywhere on the platform.</p>
      `;
      break;

    default:
      color = theme.grey2[500];
      text = newText || "None";
      description = "";
      longDescription = "";
      break;
  }

  return { color, text, description, longDescription };
}

export { getStateData };
